import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Summary from '../organisms/Origination/Summary/Summary';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { balanceInfo, clientData, indicator } from '../types/interfaces';
import Card from '../components/card/Card';
import Tabs from '../components/tabs/Tabs';
import SummaryDocuments from '../organisms/Origination/Summary/Documents';
import SummaryAmounts from '../organisms/common/SummaryAmounts';
// Removed action buttons for this view, no need to import Button/Spinner
import { Toast, useToast } from '../components/toast/toast';
import {
  getApplication,
  updateApplication,
} from '../services/ApplicationService';
import {
  capitalize,
  hasData,
  hasError,
  isSuccessfulResponse,
  getEnabledRiskCodes,
} from '../utils/Utils';
import { APPLICATION_STATUS, RISK_CODES } from '../utils/Constants';
import Modal from '../components/modal/modal';
import P from '../components/texts/p/P';
import TextArea from '../components/textarea/TextArea';
import { initialClientData } from '../utils/Constants';
import Icon from '../components/icon/Icon';
import H from '../components/texts/h/H';
import Application from '../organisms/Origination/Application';
import useRole from '../hooks/useRole';
import {
  DASHBOARD_KEYS,
  navigateAfterFlowAction,
  navigateBackToList,
  resolveListRoute,
  type DetailLocationState,
} from '../utils/listNavigationState';
import { getBalanceInfoByCode, getBalanceSheetsByCuit, getDebts } from '../services/RiskService';
import CheckLightResume from '../organisms/Risk/CheckLight/ChecklightResume';
import LidecoSemaforo from '../organisms/Risk/CheckLight/LidecoSemaforo';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import { useClientTheme } from '../context/ThemeContext';
import InstrumentationReadOnlyView from '../organisms/Instrumentation/InstrumentationReadOnlyView';
import SgrApprovalDistributionReadOnly from '../organisms/Committee/SgrApprovalDistributionReadOnly';
import { getContracts } from '../services/ApplicationService';
import RiskAnalysisPdf from '../organisms/Risk/Pdf/RiskAnalysisPdf';
import { useRiskPdf } from '../hooks/useRiskPdf';
import { useReferrerNames } from '../hooks/useReferrerNames';
import { useOfficers } from '../hooks/useOfficers';
import ApprovalHistory from '../organisms/Committee/ApprovalHistory';
import { useConfig } from '../context/ConfigContext';
import { useManagements } from '../hooks/useManagements';
import { extractPreviousBpbIndicators } from '../utils/extractPreviousBpbIndicators';
import { resolveSolicitudProducts } from '../utils/solicitudProducts';
import { formatAnalystNotesForDisplay } from '../utils/analystNotesDisplay';
import {
  getApplicationStatusKey,
  isFinishedOrRejectedStatus,
  isFinishedStatus,
  isRejectedStatus,
} from '../utils/applicationStatusUtils';
import { getProductLines } from '../services/ClientService';
import { parseSelectableProductLines, type SelectableProductLine } from '../utils/productLines';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';
import { hasCommitteeRiskTabContent } from '../utils/committeeRiskTab';
import RiskCreditoMaximoReadOnly from '../organisms/Risk/CreditoMaximo/RiskCreditoMaximoReadOnly';
import { useCommitteeCreditoMaximo } from '../hooks/useCommitteeCreditoMaximo';

const ClientApplication = () => {
  const appConfig = useConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(appConfig);
  const fiscalId = useFiscalIdConfig();
  const isUruguayEnabled = appConfig.isUruguayEnabled;
  const isInstrumentationEnabled = appConfig.isInstrumentationEnabled ?? true;
  const isMultipleApprovalInCommitteeEnabled =
    appConfig.isMultipleApprovalInCommitteeEnabled ?? false;
  const isCommitteeMemberQuorumEnabled =
    appConfig.isCommitteeMemberQuorumEnabled === true;
  const showApprovalHistoryTab =
    isMultipleApprovalInCommitteeEnabled || isCommitteeMemberQuorumEnabled;
  const showDualBalance = appConfig.isShowDualBalanceEnabled ?? false;
  const isManagementSelectionEnabled = appConfig.isManagementSelectionEnabled ?? false;
  const isMaxCreditCalculatorEnabled = appConfig.isMaxCreditCalculatorEnabled === true;
  const isQuotationsEnabled = appConfig.isQuotationsEnabled === true;
  const { managements } = useManagements(isManagementSelectionEnabled);
  const { cuit, id } = useParams();
  const location = useLocation();
  const detailState = (location.state ?? {}) as DetailLocationState;
  const status = detailState.status;
  const statusCode = detailState.statusCode;
  const listState = detailState.listState;
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigateBackToList(
      navigate,
      listState,
      resolveListRoute(listState?.dashboardKey ?? DASHBOARD_KEYS.origination),
    );
  };
  const { clientTheme } = useClientTheme();
  const { referrerNames } = useReferrerNames();
  const { officers } = useOfficers();
  const [isClientDataLoaded, setIsClientDataLoaded] = useState<boolean>(false);
  const [applicationReady, setApplicationReady] = useState<boolean>(false);
  const [clientData, setClientData] = useState<clientData>(initialClientData);
  const [, setIsSendingApplication] = useState<boolean>(false);
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const [isSaving, _setIsSaving] = useState<boolean>(false);
  const { isComite, isCommercial, isInstrumentator, isAdmin, isRiskAnalyst } = useRole();
  // Removed isRejected/isFinished usage in this view
  const [isSendApplicationModal, setIsSendApplicationModal] =
    useState<boolean>(false);

  const currentStatus = useMemo(() => {
    if (statusCode) return getApplicationStatusKey(statusCode);
    if (clientData?.status?.code) {
      return getApplicationStatusKey(clientData.status);
    }
    if (status !== undefined && status !== null && status !== '') {
      return getApplicationStatusKey(status);
    }
    if (clientData?.status?.id) {
      return getApplicationStatusKey(clientData.status);
    }
    return '';
  }, [status, statusCode, clientData?.status]);

  const isFinishedOrRejected = isFinishedOrRejectedStatus(currentStatus);
  const canViewFolder =
    isComite || isCommercial || isInstrumentator || isAdmin || isRiskAnalyst;
  const canPerformFlowActions =
    (isComite || isCommercial || isInstrumentator || isAdmin) && !isFinishedOrRejected;
  const [applicationData, setApplicationData] = useState<any>({});
  const [riskData, setRiskData] = useState<any>(undefined);
  const [previousBpbIndicators, setPreviousBpbIndicators] = useState<
    indicator[] | undefined
  >(undefined);
  const [debts, setDebts] = useState<any>({});
  const [balanceInfo, setBalanceInfo] = useState<balanceInfo | undefined>(
    undefined
  );
  const [previousBalanceInfo, setPreviousBalanceInfo] = useState<
    balanceInfo | undefined
  >(undefined);
  const [postBalanceData, setPostBalanceData] = useState({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [contracts, setContracts] = useState<any[]>([]);
  const [productLines, setProductLines] = useState<SelectableProductLine[]>([]);
  const pdfRef = useRef<HTMLDivElement>(null);
  const { isGenerating: isPdfGenerating, generatePdfFromNode } = useRiskPdf({
    fileName: `analisis_riesgo_${cuit || 'cliente'}.pdf`,
    useImprovedPdfExport: showDualBalance,
  });

  useEffect(() => {
    setPreviousBpbIndicators(extractPreviousBpbIndicators(riskData));
  }, [riskData]);

  const noopLidecoRefresh = useCallback(() => {}, []);

  useEffect(() => {
    getProductLines()
      .then((response) => {
        if (response && 'data' in response) {
          setProductLines(parseSelectableProductLines(response.data));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch product lines:', error);
      });
  }, []);

  useEffect(() => {
    if (cuit && id) {
      setIsLoading(true);
      getApplication(cuit, id).then((response) => {
        if (hasData(response)) {
          setClientData(response.data);
          setApplicationReady(true);
          setPostBalanceData(response.data.post_balance_sheet_items);
          setPreviousBalanceInfo(undefined);
          getBalanceInfoFromCode(cuit, response.data.balance_sheet_code);

          if (showDualBalance && response.data.balance_sheet_code) {
            getPreviousBalanceForDualView(cuit, response.data.balance_sheet_code);
          }

          if (response.data.risk_analysis) {
            const risks = response.data.risk_analysis.risks;
            setRiskData(risks);
            setPreviousBpbIndicators(extractPreviousBpbIndicators(risks));
          } else {
            setRiskData([]);
            setPreviousBpbIndicators(undefined);
          }
          const code = response.data.client_debt_analysis_code;
          getDebts(cuit, code).then((response) => {
            if (hasData(response) && response.status === 200) {
              setDebts(response.data);
            } else if (hasError(response).isAnyError) {
              useToast.error({
                title: 'Ocurrió un error al consultar la información.',
                id: 'risk-toast',
                description: hasError(response).isClientError
                  ? 'Error del cliente'
                  : 'Error del servidor',
                closeButton: true,
                autoClose: 5000,
              });
            }
          });

          if (isFinishedOrRejectedStatus(response.data.status)) {
            getContracts(id).then((contractsResponse) => {
              if (
                hasData(contractsResponse) &&
                isSuccessfulResponse(contractsResponse)
              ) {
                setContracts(contractsResponse.data || []);
              }
            });
          }
        }
        setIsLoading(false);
      });
    }
  }, [cuit, id, showDualBalance]);

  const getBalanceInfoFromCode = (cuit: string, balanceSheetCode: string) => {
    getBalanceInfoByCode(cuit, balanceSheetCode).then((response) => {
      if (hasData(response) && response.status === 200) {
        setBalanceInfo({
          ...response.data,
          ...formatNumbers(response.data),
        });
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error al consultar la información del balance.',
          id: 'risk-toast',
          description: hasError(response).isClientError
            ? 'Error del cliente'
            : 'Error del servidor',
          closeButton: true,
          autoClose: 5000,
        });
      }
    });
  };

  const getPreviousBalanceForDualView = (cuitParam: string, currentBalanceCode: string) => {
    getBalanceSheetsByCuit(cuitParam).then((response) => {
      if (hasData(response) && response.status === 200) {
        const currentIndex = response.data.findIndex(
          (balance: { code: string }) => balance.code === currentBalanceCode
        );
        if (currentIndex !== -1 && response.data.length > currentIndex + 1) {
          const previousBalance = response.data[currentIndex + 1];
          setPreviousBalanceInfo({
            ...previousBalance,
            ...formatNumbers(previousBalance),
          });
        }
      }
    });
  };

  const handleDownloadPdf = async () => {
    await generatePdfFromNode(pdfRef.current);
  };

  const formatNumbers = (obj: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]): [string, any] => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return [key, formatNumbers(value)];
        }
        // Convertir números a strings para que se muestren correctamente en los inputs
        if (typeof value === 'number') {
          return [key, String(value)];
        }
        return [key, value];
      })
    );
  };

  const shouldShowInstrumentationTab = useMemo(() => {
    return isInstrumentationEnabled && isFinishedOrRejectedStatus(currentStatus);
  }, [currentStatus, isInstrumentationEnabled]);

  const hasReachedInstrumentation = useMemo(() => {
    if (isFinishedStatus(currentStatus)) {
      return true;
    }
    if (isRejectedStatus(currentStatus)) {
      return (
        (clientData.instrumentation_id &&
          clientData.instrumentation_id.trim() !== '') ||
        (contracts && contracts.length > 0)
      );
    }
    return false;
  }, [currentStatus, clientData.instrumentation_id, contracts]);

  const solicitudClientData = useMemo(
    () => ({
      ...clientData,
      products: resolveSolicitudProducts(clientData),
    }),
    [clientData]
  );

  const {
    state: folderCreditoState,
    productLines: folderCreditoProductLines,
    availableAssets: folderCreditoAvailableAssets,
    loading: folderCreditoLoading,
    superaMaximo: folderCreditoSuperaMaximo,
  } = useCommitteeCreditoMaximo(
    cuit,
    id,
    isMaxCreditCalculatorEnabled,
    clientData.products,
    isQuotationsEnabled,
    0,
    applicationReady
  );

  const contentTabs = [
    {
      label: 'Solicitud',
      content: (
        <>
          <Summary
            setClientData={(e: any) => setClientData(e)}
            applicationStatus={currentStatus}
            isSaving={isSaving}
            clientData={solicitudClientData}
            onLoadClientData={(e: boolean) => setIsClientDataLoaded(e)}
            isClientDataLoaded={isClientDataLoaded}
            showButtons={false}
          />

          {/* Sección de Productos Aprobados - se muestra si hay productos Movil o distribuciones SGR */}
          {isFinishedStatus(currentStatus) &&
            ((clientData.approved_products && clientData.approved_products.length > 0) ||
             (clientData.sgr_distributions && clientData.sgr_distributions.length > 0)) && (
              <Card margin={{ top: '4' }} width='100%'>
                <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
                  <H
                    type='3'
                    variant='neutral-1'
                    withMargin={false}
                    weight='bold'
                    modifier='strong'
                  >
                    Monto Aprobado
                  </H>
                </Card>
                {clientData.approved_products && clientData.approved_products.length > 0 && (
                  <Card margin={{ top: '4' }} width='100%'>
                    <SummaryAmounts
                      clientData={clientData}
                      isManualAmount={false}
                      amounts={clientData.approved_amount || []}
                      products={clientData.approved_products}
                      localCurrencyCode={localCurrencyCode}
                    />
                  </Card>
                )}
                {clientData.sgr_distributions &&
                  clientData.sgr_distributions.length > 0 && (
                    <SgrApprovalDistributionReadOnly
                      sgrDistributions={clientData.sgr_distributions}
                    />
                  )}
              </Card>
            )}
        </>
      ),
    },
    {
      label: 'Análisis de riesgo',
      content: (
        <>
          <CheckLightResume
            data={riskData}
            showBalanceInfo={true}
            isLoading={isLoading}
            handleRiskData={(e: any) => setRiskData(e)}
            riskCodes={
              isUruguayEnabled
                ? [RISK_CODES.bpb]
                : getEnabledRiskCodes([RISK_CODES.bpb, RISK_CODES.casfog, RISK_CODES.bcra])
            }
            hideArBureauSections={isUruguayEnabled}
            debts={debts}
            documents={clientData.files}
            cuit={cuit}
            hideInfo={true}
            balanceData={balanceInfo}
            postBalanceData={postBalanceData}
            onDownloadPdf={handleDownloadPdf}
            isPdfLoading={isPdfGenerating}
            showDualBalance={showDualBalance}
            previousBalanceData={previousBalanceInfo}
            previousBpbIndicators={previousBpbIndicators}
            analystNotes={clientData.analyst_notes}
            hideNotes
          />
          {isUruguayEnabled && (
            <LidecoSemaforo
              riskData={Array.isArray(riskData) ? riskData : undefined}
              isLoading={isLoading}
              onRefresh={noopLidecoRefresh}
              hideRefresh
            />
          )}
          {isMaxCreditCalculatorEnabled && cuit && id && (
            <div style={{ marginTop: '32px' }}>
              <RiskCreditoMaximoReadOnly
                mode='risk-analysis'
                loading={folderCreditoLoading}
                creditoState={folderCreditoState}
                productLines={folderCreditoProductLines}
                superaMaximo={folderCreditoSuperaMaximo}
                localCurrencyCode={localCurrencyCode}
                isUruguayEnabled={isUruguayEnabled}
                isQuotationsEnabled={isQuotationsEnabled}
                availableAssets={folderCreditoAvailableAssets}
                solicitudClientData={solicitudClientData}
                clientReason={clientData.reason}
                summaryLayout='inline'
              />
            </div>
          )}
          <Card margin={{ top: '4' }}>
            <Card
              variant={clientTheme ? 'primary' : 'success'}
              modifier='weak'
              spacing={'2'}
            >
              <H
                type='3'
                variant='neutral-1'
                withMargin={false}
                weight='bold'
                modifier='strong'
              >
                Notas
              </H>
            </Card>
            <Card margin={{ top: '3' }}>
              <P type='2' variant='neutral' withMargin={false}>
                {formatAnalystNotesForDisplay(clientData.analyst_notes)}
              </P>
            </Card>
          </Card>
        </>
      ),
    },
    {
      label: 'Documentación completa',
      content: (
        <SummaryDocuments
          cuit={clientData.cuit}
          documents={clientData.files}
          legalEntity={clientData.basic_data.legal_entity}
          applicationId={id}
        />
      ),
    },
    ...(showApprovalHistoryTab
      ? [
          {
            label: 'Historial de aprobación',
            content: (
              <ApprovalHistory
                cuit={cuit || ''}
                applicationId={id || ''}
              />
            ),
          },
        ]
      : []),
    ...(shouldShowInstrumentationTab
      ? [
          {
            label: 'Instrumentación',
            content: (
              <InstrumentationReadOnlyView
                clientData={clientData}
                applicationId={id}
                cuit={cuit}
              />
            ),
          },
        ]
      : []),
  ];

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const handleApprove = (status: string) => {
    const useManual = applicationData.manualTotalAmounts === true;

    const formatProducts = () =>
      applicationData.applications
        ?.filter((item: any) => !!item)
        .map((item: any) => ({
          product_line_code: item.product_line_code,
          amount: {
            currency_code: item.amount.currency_code,
            value:
              typeof item.amount.value === 'string'
                ? parseFloat(item.amount.value)
                : item.amount.value,
          },
          ...(item.has_assets && {
            has_assets: item.has_assets,
            asset_code: item.asset_code,
            asset_count: item.asset_count,
          }),
        })) || [];

    const sumAmounts = (source: any[], currency: string): number => {
      return (
        source
          ?.filter((p) => p.currency_code === currency)
          .reduce((sum, p: any) => {
            const value =
              typeof p.value === 'string' ? parseFloat(p.value) : p.value;
            return sum + (isNaN(value) ? 0 : value);
          }, 0) || 0
      );
    };

    const products = formatProducts();

    const baseSource = useManual
      ? applicationData.requestedAmounts
      : products.map((p: any) => ({
          currency_code: p.amount.currency_code,
          value: p.amount.value,
        }));

    const totalARS = parseFloat(sumAmounts(baseSource, localCurrencyCode).toFixed(2));
    const totalUSD = parseFloat(sumAmounts(baseSource, 'USD').toFixed(2));

    const payload = {
      status_code: status,
      comment: modalText,
      approved_products: {
        products,
        manual_approved_total_amount_ars: totalARS,
        manual_approved_total_amount_usd: totalUSD,
      },
    };
    handleApplication(payload);
  };

  const handleApplication = (payload: any) => {
    setIsSendingApplication(true);
    const mapStatus = new Map();
    mapStatus.set(
      APPLICATION_STATUS.rejected.label,
      'Declinaste la solicitud. El comercial responsable será notificado.'
    );
    mapStatus.set(
      APPLICATION_STATUS.pending.label,
      'Solicitaste cambios en la solicitud. El comercial responsable será notificado.'
    );
    mapStatus.set(
      APPLICATION_STATUS.instrumentation.label,
      'Aceptaste la documentación. El comercial responsable será notificado.'
    );

    updateApplication(cuit || '', payload, id || '').then((response) => {
      if (isSuccessfulResponse(response)) {
        useToast.success({
          title: mapStatus.get(payload.status_code),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, detailState);
        }, 1000);
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error inesperado. Intentá nuevamente.',
          id: 'application-toast',
          description: hasError(response).isClientError
            ? 'Error de cliente'
            : 'Error técnico',
          closeButton: false,
          autoClose: 5000,
        });
      }
      setIsSendingApplication(false);
    });
  };

  const sendApplicationModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          La solicitud pasará a la instancia de Instrumentación. Podés editar
          los montos y los productos, y dejar un mensaje para el productor.
        </P>
        <Card margin={{ top: '3' }}>
          <Application
            cuit={cuit || ''}
            id={String(id)}
            onSave={() => {}}
            applicationStatus={currentStatus}
            onPreviousClick={() => {}}
            isSaving={false}
            manualTotalAmounts={clientData.manual_total_amount}
            requestedAmounts={clientData.requested_amount}
            hideInfo={true}
            applications={clientData.products}
            onChange={(e: any) => setApplicationData(e)}
            clientData={clientData}
            signers={clientData.signers as any}
          />

          <Card margin={{ top: '3' }}>
            <TextArea
              placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
              maxLengthHelpText={false}
              onChange={(e) => setModalText(e.target.value)}
            ></TextArea>
          </Card>
        </Card>
      </Card>
    );
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <RiskAnalysisPdf
        ref={pdfRef}
        clientData={clientData}
        riskData={riskData}
        balanceData={balanceInfo}
        postBalanceData={postBalanceData}
        debts={debts}
        documents={clientData.files}
        cuit={cuit}
        referrerNames={referrerNames}
        officers={officers}
        productLines={productLines}
        managements={managements}
        isManagementSelectionEnabled={isManagementSelectionEnabled}
        showDualBalance={showDualBalance}
        previousBalanceData={previousBalanceInfo}
        previousBpbIndicators={previousBpbIndicators}
        sgrDistributions={clientData.sgr_distributions}
        isUruguayEnabled={isUruguayEnabled}
        localCurrencyCode={localCurrencyCode}
        creditoMaximoState={folderCreditoState}
        creditoProductLines={folderCreditoProductLines}
        creditoSuperaMaximo={folderCreditoSuperaMaximo}
        isQuotationsEnabled={isQuotationsEnabled}
        creditoAvailableAssets={folderCreditoAvailableAssets}
      />
      {canViewFolder && (
        <>
          <Toast id='application-toast' />
          {canPerformFlowActions && (
            <>
              <Modal
                isOpen={isRejectApplication}
                onClose={() => setIsRejectApplication(false)}
                title={'Declinar o solicitar cambios'}
                primaryText='Solicitar cambios'
                primaryVariant='primary'
                secondaryText='Declinar solicitud'
                secondaryVariant='secondary'
                onSecondaryAction={() => {
                  setIsRejectApplication(false);
                  handleApplication({
                    status_code: APPLICATION_STATUS.rejected.label,
                    comment: modalText,
                  });
                }}
                onPrimaryAction={() => {
                  setIsRejectApplication(false);
                  handleApplication({
                    status_code: APPLICATION_STATUS.pending.label,
                    comment: modalText,
                  });
                }}
                children={renderRejectModal()}
              />
              <Modal
                width='1000px'
                isOpen={isSendApplicationModal}
                onClose={() => setIsSendApplicationModal(false)}
                title={'Aprobar solicitud'}
                primaryText='Aprobar solicitud'
                primaryVariant='primary'
                secondaryText='Cancelar'
                secondaryVariant='secondary'
                onSecondaryAction={() => {
                  setIsSendApplicationModal(false);
                }}
                onPrimaryAction={() => {
                  setIsSendApplicationModal(false);
                  handleApprove(APPLICATION_STATUS.instrumentation.label);
                }}
                children={sendApplicationModal()}
              />
            </>
          )}
          <Card display='flex' margin={{ left: '2', bottom: '4' }} gap='10px'>
            <Icon
              name='chevron_left_outline'
              height={24}
              width={24}
              cursor='pointer'
              onClick={handleGoBack}
            />
            <H withMargin={false} type='2' weight='bold' modifier='strong'>
              Solicitud de {capitalize(clientData?.basic_data?.company_name)} ({' '}
              {fiscalId.format(clientData.cuit)} )
            </H>
          </Card>
          {(() => {
            const isRiskTabDisabled = !hasCommitteeRiskTabContent(riskData, {
              isUruguayEnabled,
            });
            const isInstrumentationTabDisabled =
              shouldShowInstrumentationTab && !hasReachedInstrumentation;

            const disabledTabs: number[] = [];
            if (isRiskTabDisabled) {
              disabledTabs.push(1);
            }
            if (isInstrumentationTabDisabled) {
              disabledTabs.push(3);
            }

            return <Tabs tabs={contentTabs} disabledTabs={disabledTabs} />;
          })()}
        </>
      )}
    </Card>
  );
};

export default ClientApplication;
